import * as React from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import { visuallyHidden } from "@mui/utils";
import { ITable } from "./@types";

type Order = "asc" | "desc";

interface HeadCell {
  disablePadding: boolean;
  id: string | number;
  label: string;
  numeric: boolean;
}

interface EnhancedTableProps {
  headCells: readonly HeadCell[];
  onRequestSort: (event: React.MouseEvent<unknown>, property: any) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
  minTableHeadWidth?: string;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const {
    headCells,
    order,
    orderBy,
    minTableHeadWidth,
    onRequestSort,
  } = props;
  const createSortHandler =
    (property: any) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={{ minWidth: minTableHeadWidth }}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export default function EnhancedTable(props: ITable) {
  const {
    children,
    TableRows,
    page,
    tableItems,
    tableHead,
    rowsPerPageOptions,
    pagesCount,
    rowsPerPage,
    minTableHeadWidth,
    handleChangePage,
    handleChangeRowsPerPage,
    handleRequestSortHeader,
  } = props;
  const [order, setOrder] = React.useState<Order>("asc");
  const [orderBy, setOrderBy] = React.useState<string>("");

  const handleRequestSort = (_: React.MouseEvent<unknown>, property: any) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  React.useEffect(() => {
    handleRequestSortHeader(order, orderBy);
  }, [order, orderBy]);

  return (
    <Box sx={{ width: "100%", border: "1px solid rgba(224, 224, 224, 1)" }}>
      <Paper sx={{ width: "100%", mb: 2, boxShadow: "none" }}>
        <Box sx={{ p: 1.7 }}>{children}</Box>
        <TableContainer>
          <Table
            sx={{ minWidth: 1200 }}
            aria-labelledby="tableTitle"
            size="medium"
          >
            <EnhancedTableHead
              headCells={tableHead}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={tableItems.length}
              minTableHeadWidth={minTableHeadWidth}
            />
            <TableBody>
              {TableRows}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          showFirstButton showLastButton
          component="div"
          rowsPerPageOptions={rowsPerPageOptions}
          count={pagesCount}
          rowsPerPage={Number(rowsPerPage)}
          page={page === 0 ? page : page - 1}
          onPageChange={(event, newPage) => handleChangePage(event, newPage + 1)}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelDisplayedRows={
            ({ from, to, count }) => `${from}/${to} of ${count}`
          }
        />
      </Paper>
    </Box>
  );
}
